import React from "react";
import {TextField as MuiMoneyField} from "@material-ui/core";
import Cookies from "js-cookie";

import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import API from "@beardeddevops/react.api";
import {toast} from "react-toastify";
import moment from "moment";
import {
	formatLayawayMoney,
	layawayRefundDue,
	layawayRefundDueMismatch,
} from "../../utils/layawayRefundDue";
import {isProcessorTender, isProcessorRefundMethod, isZelleTender, isZelleRefundMethod, derivePaymentMethodLabel, paymentMethodSlug} from "../../utils/paymentMethodLabel";

function parseRestockFeeInput(raw) {
	if (raw === undefined || raw === null || String(raw).trim() === "") {
		return 0;
	}
	const n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ""));
	return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function sanitizeRestockFeeInput(value) {
	const s = String(value ?? "");
	const cleaned = s.replace(/[^0-9.]/g, "");
	const parts = cleaned.split(".");
	if (parts.length <= 1) {
		return cleaned;
	}
	return parts[0] + "." + parts.slice(1).join("").slice(0, 2);
}

function isRefundablePayment(payment, refundedPaymentKeys) {
	const amt = parseFloat(payment?.amount);
	if (!Number.isFinite(amt) || amt <= 0.00001) {
		return false;
	}
	if (paymentMethodSlug(payment) === "adjustment") {
		return false;
	}
	const pk = payment?.pkey;
	if (pk != null && refundedPaymentKeys && refundedPaymentKeys.has(Number(pk))) {
		return false;
	}
	return true;
}

function restockableEntry(entry) {
	const type = String(entry?.type || entry?.item_type || "").toLowerCase();
	return type === "item" || type === "consignment";
}

function entryLayawayType(entry) {
	const type = String(entry?.type || entry?.item_type || "").toLowerCase();
	if (type === "consignment") {
		return "layaway_consignment";
	}
	if (type === "product") {
		return "layaway_product";
	}
	return "layaway_item";
}

export default class LayawayPaymentRefund extends Framework.Components.Object {
	constructor(props) {
		super(props, Types.Layaways_Payment_Refunds._name);
		this.state.refund_amount = 0;
		this.state.method = "cash";
		this.state.has_card_payments = false;
		this.state.has_zelle_payments = false;
		this.state.payments = [];
		this.state.restock_items = [];
	}

	componentDidMount() {
		const refundedKeys = new Set(
			(this.props.refundedPaymentKeys || []).map((k) => Number(k)).filter((n) => Number.isFinite(n) && n > 0)
		);
		const payments = (this.props.payments || [])
			.filter((p) => isRefundablePayment(p, refundedKeys))
			.map((p) => ({
				...p,
				selected: false,
				restocking_fee: "",
				restocking_fee_error: "",
			}));

		const restock_items = (this.props.restockItems || [])
			.filter((e) => restockableEntry(e))
			.map((e) => ({
				...e,
				true_pkey: e.true_pkey ?? e.pkey,
				layaway_type: entryLayawayType(e),
				restock: false,
				quantity: Math.max(1, parseInt(e.quantity, 10) || 1),
			}));

		const has_card_payments = payments.some((p) => isProcessorTender(p));
		const has_zelle_payments = payments.some((p) => isZelleTender(p));
		const allProcessor = payments.length > 0 && payments.every((p) => isProcessorTender(p));
		const allZelle = payments.length > 0 && payments.every((p) => isZelleTender(p));

		const patch = {
			payments,
			restock_items,
			has_card_payments,
			has_zelle_payments,
			layaway: this.props.layaway || this.props.parent,
			method: allProcessor ? "card" : allZelle ? "zelle" : "cash",
		};

		const cookieReg = Cookies.getJSON("register");
		const emptyReg =
			this.state.register === "" ||
			this.state.register === undefined ||
			this.state.register === null;
		if (emptyReg && cookieReg !== undefined && cookieReg !== null && cookieReg !== "") {
			patch.register = cookieReg;
		}

		this.setState(patch, () => this.recalculatePreview());
	}

	recalculatePreview = () => {
		const payments = (this.state.payments || []).map((row) => {
			if (!row.selected) {
				return {...row, restocking_fee_error: ""};
			}
			const amt = Math.round(parseFloat(row.amount || 0) * 100) / 100;
			const feeNum = parseRestockFeeInput(row.restocking_fee);
			let err = "";
			if (feeNum > amt + 0.00001) {
				err = `Cannot exceed payment amount ($${amt.toFixed(2)}).`;
			}
			return {...row, restocking_fee_error: err};
		});
		const selected = payments.filter((i) => i.selected);
		let totalCash = 0;
		selected.forEach((row) => {
			const amt = Math.round(parseFloat(row.amount || 0) * 100) / 100;
			const fee = parseRestockFeeInput(row.restocking_fee);
			totalCash += Math.max(0, Math.round((amt - fee) * 100) / 100);
		});
		this.setState({
			payments,
			refund_amount: Math.round(totalCash * 100) / 100,
		});
	};

	handleSelectAll = (select) => {
		const payments = (this.state.payments || []).map((row) => ({...row, selected: !!select}));
		this.setState({payments}, () => this.recalculatePreview());
	};

	handleSelectionToggle = (pkey) => {
		const payments = (this.state.payments || []).map((row) =>
			row.pkey === pkey ? {...row, selected: !row.selected} : row
		);
		this.setState({payments}, () => this.recalculatePreview());
	};

	handleLineRestockingFeeChange = (pkey, value) => {
		const clean = sanitizeRestockFeeInput(value);
		const payments = (this.state.payments || []).map((row) =>
			row.pkey === pkey ? {...row, restocking_fee: clean} : row
		);
		this.setState({payments}, () => this.recalculatePreview());
	};

	handleRestockToggle = (pkey, val) => {
		const restock_items = (this.state.restock_items || []).map((row) =>
			(row.true_pkey ?? row.pkey) === pkey ? {...row, restock: val} : row
		);
		this.setState({restock_items});
	};

	handleSubmit = async (event) => {
		await this.setState({loading: true});
		this.state.layaway = this.props.layaway || this.props.parent;

		const selected = (this.state.payments || []).filter((i) => i.selected);
		if (selected.length === 0) {
			API.ToastId = toast.error("You must select at least one payment to refund", {
				position: toast.POSITION.TOP_CENTER,
			});
			await this.setState({loading: false});
			return;
		}

		for (const row of selected) {
			if (row.restocking_fee_error) {
				API.ToastId = toast.error("Fix restocking fee errors before refunding.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
			const amt = Math.round(parseFloat(row.amount || 0) * 100) / 100;
			const fee = parseRestockFeeInput(row.restocking_fee);
			if (fee > amt + 0.00001) {
				API.ToastId = toast.error("Restocking fee cannot exceed a payment amount.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
		}

		const method = String(this.state.method || "").toLowerCase();
		if (isProcessorRefundMethod(method)) {
			const nonCard = selected.find((p) => !isProcessorTender(p));
			if (nonCard) {
				API.ToastId = toast.error("Card refunds can only include card, debit, or terminal payments.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
		}
		if (isZelleRefundMethod(method)) {
			const processor = selected.find((p) => isProcessorTender(p));
			if (processor) {
				API.ToastId = toast.error("Zelle refunds cannot include card or debit payments. Refund those with Card.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
		}

		const reg = this.state.register;
		if (reg === "" || reg === undefined || reg === null) {
			API.ToastId = toast.error("Register is required for refunds.", {
				position: toast.POSITION.TOP_CENTER,
			});
			await this.setState({loading: false});
			return;
		}

		let totalsCash = 0;
		this.state.payments = selected.map((row) => {
			const fee = parseRestockFeeInput(row.restocking_fee);
			const amt = Math.round(parseFloat(row.amount || 0) * 100) / 100;
			totalsCash += Math.max(0, Math.round((amt - fee) * 100) / 100);
			return {
				pkey: row.pkey,
				restocking_fee: fee.toFixed(2),
			};
		});
		this.state.amount = totalsCash.toFixed(2);
		this.state.restock_items = (this.state.restock_items || [])
			.filter((r) => r.restock)
			.map((r) => ({
				true_pkey: r.true_pkey ?? r.pkey,
				layaway_type: r.layaway_type || entryLayawayType(r),
				restock: true,
				quantity: Math.max(1, parseInt(r.quantity, 10) || 1),
			}));

		await this.setState({loading: false});
		await super.handleSubmit(event);
	};

	form = () => {
		const {properties} = this.model;
		const object = this.state;
		const refundDue = layawayRefundDue(this.props.refundDue ?? 0);
		const selectedCount = (this.state.payments || []).filter((i) => i.selected).length;
		const allSelected =
			(this.state.payments || []).length > 0 &&
			(this.state.payments || []).every((i) => i.selected);
		const refundMismatch = layawayRefundDueMismatch(refundDue, this.state.refund_amount || 0);

		const paymentList = (this.state.payments || []).map((payment) => {
			const amt = Math.round(parseFloat(payment.amount || 0) * 100) / 100;
			const fee = parseRestockFeeInput(payment.restocking_fee);
			const cash = payment.selected ? Math.max(0, Math.round((amt - fee) * 100) / 100) : null;
			const label =
				(payment.description ? payment.description + " · " : "") +
				"$" +
				amt.toFixed(2) +
				" · " +
				(derivePaymentMethodLabel(payment) || "—") +
				(payment.created
					? " · " + moment(payment.created).local().format("MM/DD/YYYY")
					: "");

			return (
				<div key={payment.pkey} className={"row align-items-start mb-2"}>
					<div className={"col-5"}>
						<div className="form-group mb-0">
							<Framework.Elements.Checkbox
								value={!!payment.selected}
								property={properties.method}
								error={object.method_error}
								update={() => this.handleSelectionToggle(payment.pkey)}
								color='default'
								className='refund-checkbox'
								label={label}
							/>
						</div>
					</div>
					<div className={"col-3"}>
						<div className="form-group mb-0">
							<MuiMoneyField
								size="small"
								fullWidth
								variant="outlined"
								label="Restock fee"
								value={payment.restocking_fee ?? ""}
								error={!!payment.restocking_fee_error}
								helperText={
									payment.restocking_fee_error
									|| "Kept by store; reduces cash back only (not layaway balance)."
								}
								onChange={(e) => this.handleLineRestockingFeeChange(payment.pkey, e.target.value)}
								inputProps={{inputMode: "decimal", "aria-label": "Payment restocking fee"}}
							/>
						</div>
					</div>
					<div className={"col-4"}>
						<div className="small text-muted" style={{marginTop: 6}}>
							Cash back (est.)
						</div>
						<div style={{fontWeight: 600}}>
							{payment.selected && cash != null ? `$${cash.toFixed(2)}` : "—"}
						</div>
					</div>
				</div>
			);
		});

		const restockList = (this.state.restock_items || []).map((item) => {
			const pk = item.true_pkey ?? item.pkey;
			return (
				<div key={pk} className={"row align-items-center mb-2"}>
					<div className={"col-8"}>
						<div className="small">
							{item.name || "Item"} · qty {item.quantity || 1}
							{item.type || item.item_type ? ` · ${item.type || item.item_type}` : ""}
						</div>
					</div>
					<div className={"col-4"}>
						<Framework.Elements.ButtonSwitch
							name={"restock"}
							property={{
								name: `layaway_refund_restock_${pk}`,
								label: "Restock",
							}}
							value={!!item.restock}
							error={object.restock_error}
							update={(_property, value) => this.handleRestockToggle(pk, value)}
							labelTrue={"Yes"}
							labelFalse={"No"}
							project={'pos'}
						/>
					</div>
				</div>
			);
		});

		return (
			<fieldset>
				<Framework.Elements.LoadingSpinner isLoading={object.loading} />
				{refundDue > 0.005 ? (
					<div className="alert alert-warning" role="alert" style={{marginBottom: 16}}>
						<strong>Refund due on this layaway: ${formatLayawayMoney(refundDue)}</strong>
						<div className="small" style={{marginTop: 6}}>
							Select the payment(s) to refund. Use a <strong>restocking fee</strong> on each payment when the
							payment is larger than the amount you need to return — the fee is kept by the store and only
							the cash-back portion reverses on the layaway balance.
						</div>
					</div>
				) : null}
				<h4 className="section-title">Refund payments</h4>
				<p className="text-muted small">
					Select one or more payments to return. Enter a <strong>restocking fee</strong> to refund less than
					the full payment (cash back = payment minus fee). The fee is <strong>not</strong> returned to the
					customer and <strong>does not</strong> credit the layaway balance — only cash back does.
					Inventory restock below is separate and does not change cash back.
				</p>
				<div className="mb-2">
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => this.handleSelectAll(!allSelected)}
					>
						{allSelected ? "Deselect all" : "Select all"}
					</button>
				</div>
				<div className={"row font-weight-bold small text-muted mb-1"}>
					<div className={"col-5"}>Payment</div>
					<div className={"col-3"}>Restock fee</div>
					<div className={"col-4"}>Est. cash back</div>
				</div>
				{paymentList.length ? paymentList : (
					<p className="text-muted">No refundable payments on this layaway.</p>
				)}
				<hr />
				<div className={"row"}>
					<div className={"col-md-6"}>
						<div
							className="p-3 rounded border"
							style={refundMismatch != null
								? {backgroundColor: "#fff3cd", borderColor: "#ffc107"}
								: {backgroundColor: "#f8f9fa", borderColor: "#dee2e6"}}
						>
							<div className="d-flex justify-content-between">
								<span>Total cash back (est.)</span>
								<strong>${Number(this.state.refund_amount || 0).toFixed(2)}</strong>
							</div>
							{refundDue > 0.005 ? (
								<div className="d-flex justify-content-between small text-muted mt-1">
									<span>Refund due on layaway</span>
									<span>${formatLayawayMoney(refundDue)}</span>
								</div>
							) : null}
							{refundMismatch != null ? (
								<div className="small text-danger mt-2">
									{refundMismatch > 0
										? `Selected cash back is $${formatLayawayMoney(refundMismatch)} more than the refund due.`
										: `Selected cash back is $${formatLayawayMoney(Math.abs(refundMismatch))} less than the refund due.`}
								</div>
							) : refundDue > 0.005 && selectedCount > 0 ? (
								<div className="small text-success mt-2">
									Selected cash back matches the refund due.
								</div>
							) : null}
							<div className="small text-muted mt-2">
								{selectedCount} payment{selectedCount === 1 ? "" : "s"} selected
							</div>
						</div>
					</div>
				</div>

				{(this.state.restock_items || []).length > 0 && (
					<>
						<hr />
						<h4 className="section-title">Restock inventory</h4>
						<p className="text-muted small">
							Optionally return merchandise quantity to inventory. This does not change
							cash back.
						</p>
						{restockList}
					</>
				)}

				<div className={"row mt-2"}>
					<div className={"col-md-6"}>
						<div className={"form-group"}>
							<Framework.Elements.Select
								shouldLoad={true}
								update={this.handleSelectPropertyUpdate}
								property={{...properties.method, class: false, name: "method"}}
								error={object.method_error}
								value={object.method}
								objects={[
									{pkey: "cash", name: "Cash"},
									...(this.state.has_card_payments
										? [{pkey: "card", name: "Card"}]
										: []),
									...(this.state.has_zelle_payments
										? [{pkey: "zelle", name: "Zelle"}]
										: []),
									{pkey: "other", name: "Other"},
								]}
								text={"name"}
							/>
						</div>
						<div className={"form-group"}>
							<Framework.Elements.Select
								shouldLoad={true}
								object={object.registers}
								value={object.register}
								error={object.register_error}
								model={Types.Registers._name}
								property={properties.register}
								label={"Register"}
								update={this.handleSelectPropertyUpdate}
							/>
						</div>
						<div className={"form-group"}>
							<Framework.Elements.TextField
								name={"note"}
								label={"Note"}
								multiline={"3"}
								update={this.handleTextFieldPropertyUpdate}
								error={object.note_error}
								property={properties.note}
								value={object.note}
							/>
						</div>
					</div>
				</div>
			</fieldset>
		);
	};
}
